import { t } from "../lib/i18n";
import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer, View } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import api, { getPaymentsData } from '../api';
import Tooltip from './Tooltip';
import CalendarEventModal from './CalendarEventModal';
import Tabs, { Tab } from './Tabs';
import UpcomingPaymentsSection from './UpcomingPaymentsSection';
import OverduePaymentsSection from './OverduePaymentsSection';

const localizer = momentLocalizer(moment);

interface CalendarEvent {
    title: string;
    start: Date;
    end: Date;
    allDay?: boolean;
    resource?: {
        type: string;
        id: string;
        property_id?: string;
        lease_id?: string;
        custom_id?: string;
        custom_property_id?: string;
    };
}

export const CalendarView: React.FC = () => {
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [upcomingPayments, setUpcomingPayments] = useState<any[]>([]);
    const [overduePayments, setOverduePayments] = useState<any[]>([]);
    const [view, setView] = useState<View>('month'); // State for current calendar view
    const [date, setDate] = useState<Date>(new Date()); // State for current calendar date

    useEffect(() => {
        const fetchEventsAndPayments = async () => {
            setLoading(true);
            setError(null); // Clear previous errors

            // Calculate start and end dates based on current view and date
            let startDate: string;
            let endDate: string;

            if (view === 'month') {
                startDate = moment(date).startOf('month').format('YYYY-MM-DD');
                endDate = moment(date).endOf('month').format('YYYY-MM-DD');
            } else if (view === 'week') {
                startDate = moment(date).startOf('week').format('YYYY-MM-DD');
                endDate = moment(date).endOf('week').format('YYYY-MM-DD');
            } else if (view === 'day') {
                startDate = moment(date).startOf('day').format('YYYY-MM-DD');
                endDate = moment(date).endOf('day').format('YYYY-MM-DD');
            } else { // Default to a reasonable range if view is 'agenda' or unknown
                startDate = moment(date).subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
                endDate = moment(date).add(1, 'month').endOf('month').format('YYYY-MM-DD');
            }

            try {
                const [eventsResponse, paymentsResponse] = await Promise.all([
                    api.get('/calendar/events', { params: { start_date: startDate, end_date: endDate } }),
                    getPaymentsData(startDate, endDate) // Pass startDate and endDate to getPaymentsData
                ]);

                const formattedEvents = eventsResponse.data.map((event: any) => ({
                    ...event,
                    start: new Date(event.start),
                    end: new Date(event.end),
                }));
                setEvents(formattedEvents);

                setUpcomingPayments(paymentsResponse.upcoming_payments || []);
                setOverduePayments(paymentsResponse.overdue_payments || []);

            } catch (err) {
                console.error("Error fetching calendar events or payment data:", err);
                setError('Failed to load calendar events or payment data.');
            } finally {
                setLoading(false);
            }
        };

        fetchEventsAndPayments();
    }, [date, view]); // Re-fetch when date or view changes

    const handleSelectEvent = (event: CalendarEvent) => {
        setSelectedEvent(event);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedEvent(null);
    };

    if (loading) {
        return <div className="container mx-auto p-4">{t("calendarView.k1")}</div>;
    }

    if (error) {
        return <div className="container mx-auto p-4 text-danger">{t("calendarView.k2")} {error}</div>;
    }

    return (
        <div className="container mx-auto p-4 bg-bg-primary min-h-screen">
            <div className="flex items-center mb-6">
                <h1 className="text-3xl font-bold text-text-secondary">{t("calendarView.k3")}</h1>
                <Tooltip text="This calendar provides a visual overview of all your critical dates, such as lease expirations, rent payments, and other important events. This helps you stay on top of your portfolio and never miss a deadline." />
            </div>

            <Tabs initialActiveTab="Calendar">
                <Tab label="Calendar">
                    <div className="bg-bg-secondary p-4 rounded shadow border border-border-primary" style={{ height: '70vh' }}>
                        <Calendar
                            localizer={localizer}
                            events={events}
                            startAccessor="start"
                            endAccessor="end"
                            style={{ flex: 1 }}
                            onSelectEvent={handleSelectEvent}
                            view={view} // Controlled view
                            date={date} // Controlled date
                            onView={(newView) => setView(newView)} // Update view state
                            onNavigate={(newDate) => setDate(newDate)} // Update date state
                        />
                    </div>
                </Tab>
                <Tab label="Upcoming Payments">
                    <UpcomingPaymentsSection payments={upcomingPayments} />
                </Tab>
                <Tab label="Overdue Payments">
                    <OverduePaymentsSection payments={overduePayments} />
                </Tab>
            </Tabs>

            {isModalOpen && (
                <CalendarEventModal event={selectedEvent} onClose={handleCloseModal} />
            )}
        </div>
    );
};
